type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void> | void;
};

type Listener = (prompt: BeforeInstallPromptEvent | null) => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let initialized = false;
const listeners = new Set<Listener>();

export function isStandaloneDisplayMode() {
  if (typeof window === 'undefined') {
    return false;
  }

  const standaloneMediaQuery = window.matchMedia?.('(display-mode: standalone)');
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  const iOSStandalone = navigatorWithStandalone.standalone === true;

  return Boolean(standaloneMediaQuery?.matches || iOSStandalone);
}

export function isIosBrowser() {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  );
}

export function isIosSafari() {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent;
  const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/.test(userAgent);

  return isIosBrowser() && isSafari;
}

export function getDeferredInstallPrompt() {
  return deferredPrompt;
}

export function subscribeInstallPrompt(listener: Listener) {
  listeners.add(listener);
  listener(deferredPrompt);

  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  for (const listener of listeners) {
    listener(deferredPrompt);
  }
}

export function clearDeferredInstallPrompt() {
  deferredPrompt = null;
  notify();
}

/** Capture beforeinstallprompt as early as possible (before login UI mounts). */
export function initPwaInstallCapture() {
  if (typeof window === 'undefined' || initialized) {
    return;
  }

  initialized = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export type { BeforeInstallPromptEvent };
